import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { paymentsService, partnersService } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';

const PaymentSendPage = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [eligibleEarnings, setEligibleEarnings] = useState([]);
  const [selectedEarnings, setSelectedEarnings] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [sendNotification, setSendNotification] = useState(true);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  useEffect(() => {
    loadPartners();
  }, []);

  useEffect(() => {
    if (selectedPartnerId) {
      loadEligibleEarnings(selectedPartnerId);
    } else {
      setEligibleEarnings([]);
      setSelectedEarnings([]);
      setTotalAmount(0);
    }
  }, [selectedPartnerId]);

  useEffect(() => {
    // Seçili hak edişlerin toplamını hesapla
    const total = selectedEarnings.reduce((sum, earningId) => {
      const earning = eligibleEarnings.find(e => e.id === earningId);
      return sum + (earning ? parseFloat(earning.net_amount || 0) : 0);
    }, 0);
    setTotalAmount(total);
  }, [selectedEarnings, eligibleEarnings]);

  const loadPartners = async () => {
    try {
      const response = await partnersService.getAllPartners({ 
        status: 'approved',
        limit: 1000 
      });
      if (response.success) {
        setPartners(response.data || []);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadEligibleEarnings = async (partnerId) => {
    try {
      setLoadingEarnings(true);
      const response = await paymentsService.getEligibleEarnings(partnerId);
      if (response.success) {
        setEligibleEarnings(response.data || []);
        setSelectedEarnings([]); // Reset selection
      } else {
        toast.error(response.error || 'Uygun hak edişler alınamadı');
      }
    } catch (error) {
      console.error('Error loading eligible earnings:', error);
      toast.error('Uygun hak edişler yüklenirken bir hata oluştu');
    } finally {
      setLoadingEarnings(false);
    }
  };

  const handleEarningToggle = (earningId) => {
    setSelectedEarnings(prev => {
      if (prev.includes(earningId)) {
        return prev.filter(id => id !== earningId);
      } else {
        return [...prev, earningId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEarnings.length === eligibleEarnings.length) {
      setSelectedEarnings([]);
    } else {
      setSelectedEarnings(eligibleEarnings.map(e => e.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPartnerId) {
      toast.error('Lütfen bir partner seçin');
      return;
    }

    if (selectedEarnings.length === 0) {
      toast.error('Lütfen en az bir hak ediş seçin');
      return;
    }

    if (totalAmount <= 0) {
      toast.error('Toplam tutar geçerli değil');
      return;
    }

    if (!window.confirm(`${selectedPartnerId ? partners.find(p => p.id === parseInt(selectedPartnerId))?.name : 'Partner'} için ${totalAmount.toFixed(2)} TL ödeme göndermek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await paymentsService.sendPayment({
        partner_id: parseInt(selectedPartnerId),
        earning_ids: selectedEarnings,
        total_amount: totalAmount,
        send_notification: sendNotification,
        notes: notes || null
      });

      if (response.success) {
        toast.success('Ödeme başarıyla gönderildi');
        navigate('/payments');
      } else {
        toast.error(response.error || 'Ödeme gönderilemedi');
      }
    } catch (error) {
      console.error('Error sending payment:', error);
      toast.error('Ödeme gönderilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Ödeme Gönder" />
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-0">Ödeme Gönder</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Partner Seçimi */}
            <div className="mb-4">
              <label className="form-label">Partner <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={selectedPartnerId}
                onChange={(e) => setSelectedPartnerId(e.target.value)}
                required
              >
                <option value="">Partner Seçin</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} - {partner.phone}
                  </option>
                ))}
              </select>
            </div>

            {/* Uygun Hak Edişler */}
            {selectedPartnerId && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <label className="form-label mb-0">Uygun Hak Edişler</label>
                  {eligibleEarnings.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSelectAll}
                    >
                      {selectedEarnings.length === eligibleEarnings.length ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                    </button>
                  )}
                </div>

                {loadingEarnings ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : eligibleEarnings.length === 0 ? (
                  <div className="alert alert-info">
                    Bu partner için ödeme yapılabilecek hak ediş bulunmamaktadır.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th width="50">
                            <input
                              type="checkbox"
                              checked={selectedEarnings.length === eligibleEarnings.length && eligibleEarnings.length > 0}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>Müşteri</th>
                          <th>Satış Tarihi</th>
                          <th>Hak Ediş Tarihi</th>
                          <th>Fatura Durumu</th>
                          <th>Uygun Gün</th>
                          <th className="text-end">Net Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleEarnings.map(earning => (
                          <tr key={earning.id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedEarnings.includes(earning.id)}
                                onChange={() => handleEarningToggle(earning.id)}
                              />
                            </td>
                            <td>
                              <div>
                                <strong>{earning.customer_name || '-'}</strong>
                                {earning.customer_phone && (
                                  <div className="text-muted small">{earning.customer_phone}</div>
                                )}
                              </div>
                            </td>
                            <td>{formatDate(earning.sale_date)}</td>
                            <td>{formatDate(earning.commission_earned_date)}</td>
                            <td>
                              {earning.invoice_exists ? (
                                <span className="badge bg-success">Fatura Yüklü</span>
                              ) : earning.can_issue_invoice === 0 ? (
                                <span className="badge bg-warning">Fatura Kesemiyor</span>
                              ) : (
                                <span className="badge bg-secondary">Fatura Yok</span>
                              )}
                            </td>
                            <td>
                              <span className="badge bg-info">{earning.days_eligible} gün</span>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(earning.net_amount)}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Toplam Tutar */}
            {selectedEarnings.length > 0 && (
              <div className="mb-4">
                <div className="alert alert-success">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><strong>Toplam Ödenecek Tutar:</strong></span>
                    <span className="fs-4 fw-bold">{formatCurrency(totalAmount)}</span>
                  </div>
                  <div className="mt-2 text-muted small">
                    Seçili hak ediş sayısı: {selectedEarnings.length}
                  </div>
                </div>
              </div>
            )}

            {/* Notification Seçeneği */}
            <div className="mb-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="sendNotification"
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="sendNotification">
                  Partner'a bildirim gönder
                </label>
              </div>
            </div>

            {/* Notlar */}
            <div className="mb-4">
              <label className="form-label">Notlar (Opsiyonel)</label>
              <textarea
                className="form-control"
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ödeme ile ilgili notlar..."
              />
            </div>

            {/* Submit Button */}
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || selectedEarnings.length === 0 || !selectedPartnerId}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Icon icon="solar:wallet-money-outline" className="me-2" />
                    Ödeme Gönder
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/payments')}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      </div>
    </MasterLayout>
  );
};

export default PaymentSendPage;

